import { useCallback, useState } from 'react';

import { coordsOf, geocode, PickupLocation } from './pickupLocation';
import {
  distanceBeyondZone,
  estimateDrivingKm,
  LatLng,
  RATES,
  testZone,
  transportCharge,
  ZoneRegion,
} from './zone';

export type TransportResult =
  | { inZone: true; municipality: string; coords: LatLng }
  | {
      inZone: false;
      coords: LatLng;
      billableKm: number;
      amount: number;
      crossing: LatLng | null;
      crossingMunicipality: string;
      /** True when a driving route was found; false when it fell back to straight line. */
      measured: boolean;
    };

/**
 * The part of the transport calculation that costs something.
 *
 * Split from `useZoneStatus` on purpose. Testing a point against the polygon is
 * local and free, so that runs wherever it is displayed. Geocoding an address
 * and routing a journey are network calls that cost money and, more to the
 * point, produce a PRICE — so they only ever run when somebody asks.
 *
 * Shared by the contract's show and edit views so the two cannot produce
 * different numbers for the same case.
 */
export function useTransportCalculator(
  region: ZoneRegion,
  /**
   * The already-resolved pickup. Passed in rather than derived here, because
   * resolving it can require fetching a `places` row for a facility death — and
   * a hook that both fetches and prices would run that lookup on every screen
   * showing a price.
   */
  pickup: PickupLocation | null,
  onCoordsResolved?: (coords: LatLng, overridden: boolean) => void,
) {
  const [result, setResult] = useState<TransportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const calculate = useCallback(async () => {
    if (!pickup) {
      setError('No address to measure from');
      return;
    }

    setBusy(true);
    setError(null);
    setResult(null);

    try {
      let coords = coordsOf(pickup.address);
      if (!coords) {
        coords = await geocode(pickup.address);
        // Hand the coordinates back so the caller can store them. Geocoding the
        // same address on every visit costs money and can drift between calls.
        onCoordsResolved?.(coords, pickup.overridden);
      }

      const zone = testZone(coords, region);
      if (zone.inZone) {
        setResult({ inZone: true, municipality: zone.municipality, coords });
        return;
      }

      try {
        const beyond = await distanceBeyondZone(coords, region);
        const charge = transportCharge(beyond.km, region);
        setResult({
          inZone: false,
          coords,
          billableKm: charge.billableKm,
          amount: charge.amount,
          crossing: beyond.crossing,
          crossingMunicipality: beyond.crossingMunicipality,
          measured: true,
        });
      } catch {
        // No route available: straight line with the road factor, clearly
        // marked. A rough number someone knows is rough beats no number.
        const charge = transportCharge(estimateDrivingKm(zone.straightLineKm), region);
        setResult({
          inZone: false,
          coords,
          billableKm: charge.billableKm,
          amount: charge.amount,
          crossing: null,
          crossingMunicipality: zone.borderMunicipality,
          measured: false,
        });
      }
    } catch (e: any) {
      setError(e?.message ?? 'Could not work out where this pickup is');
    } finally {
      setBusy(false);
    }
  }, [pickup, region, onCoordsResolved]);

  return { result, error, busy, calculate, region, rate: RATES[region], reset: () => setResult(null) };
}
